/**
 * ai/changeDetection.ts
 *
 * Detects significant market state changes that should trigger
 * an AI re-evaluation. This prevents expensive AI calls on every
 * tick while ensuring AI updates when it actually matters.
 *
 * Events are ranked by significance. Only "major" events trigger
 * an automatic AI refresh by default.
 */

import { Candle, RegimeResult, SMCResult, TrapSignal, LiquidityZone } from '../../../types';
import { SymbolThesis } from '../thesis/thesisEngine';

// ─── Change event types ───────────────────────────────────────────────────────

export type ChangeEventType =
  | 'bos_detected'
  | 'choch_detected'
  | 'sweep_reclaim'
  | 'sweep_detected'
  | 'displacement_detected'
  | 'regime_shift'
  | 'regime_confidence_jump'
  | 'trap_activated'
  | 'trap_resolved'
  | 'vwap_cross'
  | 'vwap_reclaim'
  | 'or_break'
  | 'key_level_touch'
  | 'fvg_filled'
  | 'ob_tested'
  | 'high_conviction_setup'
  | 'thesis_invalidation_risk'
  | 'new_candle';               // lowest priority — just time passing

export type ChangeSignificance = 'major' | 'minor' | 'noise';

export interface ChangeEvent {
  type:         ChangeEventType;
  significance: ChangeSignificance;
  description:  string;
  timestamp:    number;
  symbol:       string;
}

// ─── Previous state snapshot ──────────────────────────────────────────────────
// Stored per symbol to compare against new state

interface StateSnapshot {
  priceAboveVWAP:      boolean;
  priceAboveOR:        boolean;
  regimeDominant:      string;
  regimeConfidence:    number;
  smcBias:             string;
  sweepAndReverse:     boolean;
  lastBOSIdx:          number | null;
  lastCHOCHIdx:        number | null;
  activeTrapTypes:     string[];
  candleCount:         number;
  lastCandleTs:        number;
  // Stability counters — prevent single-candle false fires
  vwapAboveCount:      number;  // consecutive bars above VWAP
  vwapBelowCount:      number;  // consecutive bars below VWAP
  orBreakFiredAbove:   boolean; // OR break above already fired this session
  orBreakFiredBelow:   boolean; // OR break below already fired this session
  regimeDominantCount: number;  // consecutive bars with same dominant regime
}

const snapshots = new Map<string, StateSnapshot>();

// ─── Build snapshot from current state ───────────────────────────────────────

function buildSnapshot(
  candles:  Candle[],
  regime:   RegimeResult | null,
  smc:      SMCResult | null,
  traps:    TrapSignal[],
  prev?:    StateSnapshot
): StateSnapshot {
  const cur = candles[candles.length - 1];
  const aboveVWAP = regime ? cur.close > regime.curVWAP : false;
  const aboveOR   = regime ? cur.close > regime.levels.orHigh : false;
  const dominant  = regime?.dominant ?? 'unknown';

  // Stability counters: increment when state matches, reset on flip
  const vwapAboveCount = aboveVWAP
    ? (prev?.vwapAboveCount ?? 0) + 1
    : 0;
  const vwapBelowCount = !aboveVWAP
    ? (prev?.vwapBelowCount ?? 0) + 1
    : 0;
  const regimeDominantCount = dominant === prev?.regimeDominant
    ? (prev?.regimeDominantCount ?? 0) + 1
    : 1;

  return {
    priceAboveVWAP:      aboveVWAP,
    priceAboveOR:        aboveOR,
    regimeDominant:      dominant,
    regimeConfidence:    regime ? Math.round(regime.confidence / 10) * 10 : 0,
    smcBias:             smc?.bias ?? 'neutral',
    sweepAndReverse:     smc?.sweepAndReverse ?? false,
    lastBOSIdx:          smc?.lastBOS?.candleIdx ?? null,
    lastCHOCHIdx:        smc?.lastCHOCH?.candleIdx ?? null,
    activeTrapTypes:     traps.filter(t => t.severity !== 'None').map(t => t.type),
    candleCount:         candles.length,
    lastCandleTs:        cur.ts,
    vwapAboveCount,
    vwapBelowCount,
    orBreakFiredAbove:   prev?.orBreakFiredAbove ?? false,
    orBreakFiredBelow:   prev?.orBreakFiredBelow ?? false,
    regimeDominantCount,
  };
}

// ─── Main change detector ─────────────────────────────────────────────────────

export function detectChanges(
  symbol:  string,
  candles: Candle[],
  regime:  RegimeResult | null,
  smc:     SMCResult | null,
  traps:   TrapSignal[],
  zones:   LiquidityZone[],
  thesis:  SymbolThesis
): ChangeEvent[] {
  if (!candles.length) return [];

  const events:  ChangeEvent[] = [];
  const cur      = candles[candles.length - 1];
  const now      = Date.now();
  const prev     = snapshots.get(symbol);
  const newSnap  = buildSnapshot(candles, regime, smc, traps, prev);

  if (!prev) {
    // First run — just store snapshot, no events
    const initSnap = buildSnapshot(candles, regime, smc, traps, undefined);
    snapshots.set(symbol, initSnap);
    return [];
  }

  const add = (type: ChangeEventType, sig: ChangeSignificance, desc: string) => {
    events.push({ type, significance: sig, description: desc, timestamp: now, symbol });
  };

  // ── SMC events (highest significance) ─────────────────────────────────────

  if (smc?.sweepAndReverse && !prev.sweepAndReverse) {
    add('sweep_reclaim', 'major',
      `Sweep + reclaim confirmed on ${symbol} — highest-conviction SMC reversal signal`);
  }

  if (smc?.lastCHOCH && smc.lastCHOCH.candleIdx !== prev.lastCHOCHIdx
      && smc.lastCHOCH.candleIdx >= candles.length - 3) {
    add('choch_detected', 'major',
      `CHOCH printed: ${smc.lastCHOCH.dir} reversal signal @ ${smc.lastCHOCH.price.toFixed(2)}`);
  }

  if (smc?.lastBOS && smc.lastBOS.candleIdx !== prev.lastBOSIdx
      && smc.lastBOS.candleIdx >= candles.length - 3) {
    add('bos_detected', 'major',
      `BOS confirmed: ${smc.lastBOS.dir} continuation @ ${smc.lastBOS.price.toFixed(2)}`);
  }

  if (smc?.recentSweep && !smc.recentSweep.reclaimed
      && smc.recentSweep.candleIdx >= candles.length - 2) {
    add('sweep_detected', 'minor',
      `Liquidity sweep detected @ ${smc.recentSweep.level.toFixed(2)} — watching for reclaim`);
  }

  if (smc?.recentDisplacement && smc.recentDisplacement.candleIdx >= candles.length - 2) {
    add('displacement_detected', 'minor',
      `Displacement candle: ${smc.recentDisplacement.dir} ${smc.recentDisplacement.size.toFixed(2)}% body, ${smc.recentDisplacement.volumeMultiple.toFixed(1)}x vol`);
  }

  // ── Regime events ──────────────────────────────────────────────────────────

  // Regime shift requires 2+ consecutive bars with new dominant (hysteresis)
  if (regime && newSnap.regimeDominant !== prev.regimeDominant && newSnap.regimeDominantCount >= 2) {
    add('regime_shift', 'major',
      `Regime shift confirmed: ${prev.regimeDominant} → ${newSnap.regimeDominant} (${regime.confidence}% conf)`);
  } else if (regime && Math.abs(newSnap.regimeConfidence - prev.regimeConfidence) >= 30) {
    // Raised confidence jump threshold from 20 to 30 to reduce minor event spam
    add('regime_confidence_jump', 'minor',
      `Regime confidence changed: ${prev.regimeConfidence}% → ${newSnap.regimeConfidence}% (${regime.label})`);
  }

  // ── VWAP events — require 3 consecutive bars to confirm (prevents chop noise) ─

  if (newSnap.priceAboveVWAP && !prev.priceAboveVWAP && newSnap.vwapAboveCount >= 3) {
    add('vwap_reclaim', 'major', `VWAP reclaim confirmed (3+ bars above): price holding above VWAP @ ${regime?.curVWAP.toFixed(2)}`);
  } else if (!newSnap.priceAboveVWAP && prev.priceAboveVWAP && newSnap.vwapBelowCount >= 3) {
    add('vwap_cross', 'major', `VWAP breakdown confirmed (3+ bars below): price holding below VWAP @ ${regime?.curVWAP.toFixed(2)}`);
  }

  // ── Opening range break — fires only once per session side (prevents repeat signals) ─

  if (newSnap.priceAboveOR && !prev.priceAboveOR && !newSnap.orBreakFiredAbove && regime) {
    add('or_break', 'major', `OR breakout: price above OR High ${regime.levels.orHigh.toFixed(2)}`);
    newSnap.orBreakFiredAbove = true;
  } else if (!newSnap.priceAboveOR && prev.priceAboveOR && !newSnap.orBreakFiredBelow && regime) {
    add('or_break', 'major', `OR breakdown: price below OR Low ${regime.levels.orLow.toFixed(2)}`);
    newSnap.orBreakFiredBelow = true;
  }

  // ── Trap events ────────────────────────────────────────────────────────────

  const newTrapTypes    = newSnap.activeTrapTypes;
  const prevTrapTypes   = prev.activeTrapTypes;
  const activatedTraps  = newTrapTypes.filter(t => !prevTrapTypes.includes(t));
  const resolvedTraps   = prevTrapTypes.filter(t => !newTrapTypes.includes(t));

  activatedTraps.forEach(t => add('trap_activated', 'minor', `Trap activated: ${t}`));
  resolvedTraps.forEach(t => add('trap_resolved', 'minor', `Trap resolved: ${t}`));

  // ── Key level proximity ────────────────────────────────────────────────────

  const highSigZones = zones.filter(z => z.sig === 'High' && z.distPct < 0.2);
  if (highSigZones.length) {
    highSigZones.forEach(z =>
      add('key_level_touch', 'minor',
        `At key level: ${z.label} @ ${z.price.toFixed(2)} (${z.distPct.toFixed(2)}% away)`)
    );
  }

  // ── New candle (time-based fallback) ───────────────────────────────────────

  if (newSnap.candleCount > prev.candleCount) {
    add('new_candle', 'noise', `New candle closed @ ${cur.close.toFixed(2)}`);
  }

  // ── SMC bias shift ─────────────────────────────────────────────────────────

  if (newSnap.smcBias !== prev.smcBias && newSnap.smcBias !== 'neutral') {
    add('choch_detected', 'minor',
      `SMC bias shifted: ${prev.smcBias} → ${newSnap.smcBias}`);
  }

  // ── Thesis invalidation check ──────────────────────────────────────────────

  if (thesis.current?.invalidation) {
    const inv = thesis.current.invalidation.toLowerCase();
    // Simple heuristic: check if price is near an invalidation level mentioned in thesis
    const priceMatch = inv.match(/\d+\.?\d*/);
    if (priceMatch) {
      const invLevel = parseFloat(priceMatch[0]);
      if (invLevel > 0 && Math.abs(cur.close - invLevel) / invLevel < 0.003) {
        add('thesis_invalidation_risk', 'major',
          `Price approaching thesis invalidation level @ ${invLevel.toFixed(2)}`);
      }
    }
  }

  // Store new snapshot
  snapshots.set(symbol, newSnap);

  // Sort by significance
  const sigOrder: Record<ChangeSignificance, number> = { major: 0, minor: 1, noise: 2 };
  events.sort((a, b) => sigOrder[a.significance] - sigOrder[b.significance]);

  return events;
}

// ─── Should trigger AI refresh? ──────────────────────────────────────────────

export interface RefreshDecision {
  shouldRefresh: boolean;
  reason:        string;
  events:        ChangeEvent[];
  highestSig:    ChangeSignificance | null;
}

export function shouldTriggerRefresh(
  events:          ChangeEvent[],
  mode:            'major_only' | 'all' | 'manual',
  lastRefreshAt:   number | null,
  minCooldownMs:   number
): RefreshDecision {
  if (mode === 'manual') {
    return { shouldRefresh: false, reason: 'Manual mode', events, highestSig: null };
  }

  const now = Date.now();
  if (lastRefreshAt && (now - lastRefreshAt) < minCooldownMs) {
    const remaining = Math.round((minCooldownMs - (now - lastRefreshAt)) / 1000);
    return {
      shouldRefresh: false,
      reason: `Cooldown: ${remaining}s remaining`,
      events, highestSig: null,
    };
  }

  const majorEvents = events.filter(e => e.significance === 'major');
  const minorEvents = events.filter(e => e.significance === 'minor');

  if (majorEvents.length) {
    return {
      shouldRefresh: true,
      reason: `Major event: ${majorEvents[0].description}`,
      events, highestSig: 'major',
    };
  }

  if (mode === 'all' && minorEvents.length >= 2) {
    return {
      shouldRefresh: true,
      reason: `${minorEvents.length} minor events accumulated`,
      events, highestSig: 'minor',
    };
  }

  return {
    shouldRefresh: false,
    reason: events.length ? `Only minor/noise events (${events.length})` : 'No significant changes',
    events, highestSig: events.length ? events[0].significance : null,
  };
}

// ─── Clear snapshot on symbol change ─────────────────────────────────────────

export function clearSnapshot(symbol: string) {
  snapshots.delete(symbol);
}
